import {useCallback, useMemo, useState} from "react";
import {BackendApi} from "../api/api.ts";
import {User} from "../api/accessControl/User.ts";
import {useNotifications} from "../lib/NotificationContext.tsx";
import type {Booking} from "../pages/app/partner/pages/partnerDashboardPage/components/GeneralJournalBookings/GeneralJournalBookingsTab.model.ts";
import type {BackendAPITypes} from "../generated/blueprint.ts";

interface UseManualBookingPopupOptions {
    currentUser: User;
    businessId: string;
    onUpdated: () => Promise<void>;
}

export function useManualBookingPopup({
    currentUser,
    businessId,
    onUpdated,
}: UseManualBookingPopupOptions) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {dispatch} = useNotifications();
    const [booking, setBooking] = useState<Booking | null>(null);

    const open = useCallback(async (manualBookingId: string) => {
        try {
            const bookings = await api.Partner__getClientManualBookings(businessId, null, null);
            const found = bookings.find((row) => row.id === manualBookingId) ?? null;
            if (found === null) {
                dispatch({
                    title: "Memoriaal niet gevonden",
                    description: "Deze memoriaalboeking kon niet worden geopend.",
                    type: "error",
                });
                return;
            }
            setBooking(found as Booking);
        } catch (error) {
            console.error("Failed to open manual booking:", error);
            dispatch({
                title: "Memoriaal openen mislukt",
                description: "De memoriaalboeking kon niet worden geopend. Probeer het opnieuw.",
                type: "error",
            });
        }
    }, [api, businessId, dispatch]);

    const close = useCallback(() => {
        setBooking(null);
    }, []);

    const update = useCallback(async (
        payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEditManualBookingPostRequestBody,
    ) => {
        await api.Partner__editManualBooking(payload);
    }, [api]);

    const fetchBankAccounts = useCallback(async () => {
        return await api.Partner__entrepreneur__getBankAccounts(businessId);
    }, [api, businessId]);

    const fetchBankBalance = useCallback(async (bankAccountId: string, date: string) => {
        const balance = await api.Partner__getBankAccountBalanceAtDate({
            client_business_id: businessId,
            bank_account_id: bankAccountId,
            target_date: date,
        });
        return {
            balance: balance.balance,
            iban: balance.iban ?? "",
            institution_name: balance.institution_name,
        };
    }, [api, businessId]);

    const handleSuccess = useCallback(async () => {
        setBooking(null);
        await onUpdated();
    }, [onUpdated]);

    return {
        booking,
        open,
        close,
        update,
        fetchBankAccounts,
        fetchBankBalance,
        handleSuccess,
    };
}
